import { useAnalytics } from '@/hooks/useAnalytics'
import { useStoryBank } from '@/hooks/useStoryBank'
import { useSubscription } from '@/hooks/useSubscription'
import { supabase } from '@/lib/supabase'
import { cn } from '@/lib/utils'
import { useCallback, useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'

interface SessionRow {
  target_role: string
  competency: string
  difficulty: string
  overall_score: number | null
  completed_at: string | null
}

interface ScoreRowRaw {
  situation_score: number
  task_score: number
  action_score: number
  result_score: number
  overall_score: number
  competency_score: number
  answers: {
    questions: {
      competency: string
      is_follow_up: boolean
      sequence_number: number
    }
  }
}

interface AggregatedScores {
  situation: number
  task: number
  action: number
  result: number
  overall: number
}

interface CompetencyScoreEntry {
  competency: string
  label: string
  score: number
  sequenceNumber: number
}

interface QAItem {
  id: string
  answerId: string
  sequenceNumber: number
  questionText: string
  answerText: string
  wordCount: number
  isFollowUp: boolean
  competency: string
}

interface QAJoinRow {
  id: string
  sequence_number: number
  question_text: string
  is_follow_up: boolean
  competency: string
  answers: { id: string; answer_text: string; word_count: number }[]
}

interface RewriteData {
  example_answer: string
  what_makes_it_strong: string
}

interface RewriteState {
  status: 'idle' | 'loading' | 'done' | 'error'
  data?: RewriteData
}

interface FeedbackJson {
  summary: string
  strengths: string[]
  improvements: string[]
  rewrite_tip: string
  example_stronger_result: string | null
}

interface ReportData {
  feedback_json: FeedbackJson
  summary_text: string | null
  competencies_demonstrated: string[] | null
}

const COMPETENCY_LABELS: Record<string, string> = {
  leadership: 'Leadership',
  teamwork: 'Teamwork',
  communication: 'Communication',
  problem_solving: 'Problem Solving',
  time_management: 'Time Management',
  stakeholder_management: 'Stakeholder Management',
  attention_to_detail: 'Attention to Detail',
  mixed: 'Multi-competency',
}

const DIFFICULTY_LABELS: Record<string, string> = {
  entry: 'Entry Level',
  mid: 'Mid Level',
  senior: 'Senior Level',
}

const STAR_COMPONENTS: { key: keyof AggregatedScores; label: string; weight: string }[] = [
  { key: 'situation', label: 'Situation', weight: '20%' },
  { key: 'task', label: 'Task', weight: '15%' },
  { key: 'action', label: 'Action', weight: '40%' },
  { key: 'result', label: 'Result', weight: '25%' },
]

export default function FeedbackPage() {
  const { sessionId } = useParams<{ sessionId: string }>()
  const { isPro } = useSubscription()
  const analytics = useAnalytics()
  const { saveStory } = useStoryBank()

  const [session, setSession] = useState<SessionRow | null>(null)
  const [scores, setScores] = useState<AggregatedScores | null>(null)
  const [competencyScores, setCompetencyScores] = useState<CompetencyScoreEntry[]>([])
  const [qaItems, setQaItems] = useState<QAItem[]>([])
  const [report, setReport] = useState<ReportData | null>(null)
  const [loading, setLoading] = useState(true)
  const [reportLoading, setReportLoading] = useState(false)
  const [reportError, setReportError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [rewriteStates, setRewriteStates] = useState<Map<string, RewriteState>>(new Map())
  const [saveStates, setSaveStates] = useState<Map<string, 'idle' | 'saving' | 'saved' | 'error'>>(
    new Map()
  )

  const fetchReport = useCallback(async (sid: string) => {
    setReportLoading(true)
    setReportError(null)

    // Explicitly await session restoration before invoking — supabase.functions.invoke
    // can fire before localStorage session is restored, causing it to fall back to the
    // anon key and get a 401 from requireUser.
    const {
      data: { session },
    } = await supabase.auth.getSession()
    if (!session) {
      setReportError('Something went wrong generating your feedback.')
      setReportLoading(false)
      return
    }

    const { data: reportResponse, error: invokeErr } = await supabase.functions.invoke<{
      report: ReportData
    }>('generate-report', {
      body: { sessionId: sid },
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
    if (invokeErr || !reportResponse?.report) {
      setReportError('Something went wrong generating your feedback.')
    } else {
      setReport(reportResponse.report)
    }
    setReportLoading(false)
  }, [])

  const requestRewrite = useCallback(
    async (item: QAItem) => {
      const { answerId, competency, wordCount } = item

      setRewriteStates((prev) => new Map(prev).set(answerId, { status: 'loading' }))

      const {
        data: { session: authSession },
      } = await supabase.auth.getSession()
      if (!authSession) {
        setRewriteStates((prev) => new Map(prev).set(answerId, { status: 'error' }))
        return
      }

      const { data, error: invokeErr } = await supabase.functions.invoke<{
        rewrite: RewriteData
      }>('generate-rewrite', {
        body: { answerId },
        headers: { Authorization: `Bearer ${authSession.access_token}` },
      })

      if (invokeErr || !data?.rewrite) {
        setRewriteStates((prev) => new Map(prev).set(answerId, { status: 'error' }))
        return
      }

      setRewriteStates((prev) =>
        new Map(prev).set(answerId, { status: 'done', data: data.rewrite })
      )

      analytics.trackRewriteGenerated({
        session_id: sessionId ?? '',
        competency,
        word_count: wordCount,
      })
    },
    [sessionId, analytics]
  )

  const requestSave = useCallback(
    async (item: QAItem, title: string) => {
      const { answerId, competency } = item
      setSaveStates((prev) => new Map(prev).set(answerId, 'saving'))
      const result = await saveStory(answerId, title)
      if (result) {
        setSaveStates((prev) => new Map(prev).set(answerId, 'saved'))
        analytics.trackStorySaved({
          competency,
          difficulty: session?.difficulty ?? '',
          source: 'session_answer',
        })
      } else {
        setSaveStates((prev) => new Map(prev).set(answerId, 'error'))
      }
    },
    [saveStory, analytics, session]
  )

  useEffect(() => {
    if (!sessionId) return
    void fetchData(sessionId)
  }, [sessionId])

  async function fetchData(sid: string) {
    const { data: sessionData, error: sessionErr } = await supabase
      .from('sessions')
      .select('target_role, competency, difficulty, overall_score, completed_at')
      .eq('id', sid)
      .single()

    if (sessionErr || !sessionData) {
      setError('We could not load your session. Please try again.')
      setLoading(false)
      return
    }

    setSession(sessionData as SessionRow)

    // FK hint on the nested questions embed: answers has two relationships to questions
    // (migration 20260730000002's composite ownership FK), so an unhinted embed fails with
    // PGRST201 and rawScoreRows comes back null — silently hiding the whole score breakdown.
    const { data: rawScoreRows } = await supabase
      .from('scores')
      .select(`
        situation_score, task_score, action_score, result_score, overall_score, competency_score,
        answers!inner(
          questions!answers_question_id_fkey!inner(competency, is_follow_up, sequence_number)
        )
      `)
      .eq('session_id', sid)

    if (rawScoreRows && rawScoreRows.length > 0) {
      const rows = rawScoreRows as unknown as ScoreRowRaw[]

      const avg = (
        key: keyof Pick<
          ScoreRowRaw,
          | 'situation_score'
          | 'task_score'
          | 'action_score'
          | 'result_score'
          | 'overall_score'
          | 'competency_score'
        >
      ) => Math.round((rows.reduce((sum, s) => sum + s[key], 0) / rows.length) * 10) / 10

      const situation = avg('situation_score')
      const task = avg('task_score')
      const action = avg('action_score')
      const result = avg('result_score')
      const overall =
        Math.round((situation * 0.2 + task * 0.15 + action * 0.4 + result * 0.25) * 10) / 10

      setScores({ situation, task, action, result, overall })

      // Per-competency scores — main questions only, deduplicated by competency
      const mainRows = rows
        .filter((r) => !r.answers.questions.is_follow_up)
        .sort((a, b) => a.answers.questions.sequence_number - b.answers.questions.sequence_number)

      const seen = new Set<string>()
      const compEntries: CompetencyScoreEntry[] = []
      for (const row of mainRows) {
        const { competency, sequence_number } = row.answers.questions
        if (!seen.has(competency)) {
          seen.add(competency)
          compEntries.push({
            competency,
            label: COMPETENCY_LABELS[competency] ?? competency,
            score: Math.round(row.competency_score * 10) / 10,
            sequenceNumber: sequence_number,
          })
        }
      }
      setCompetencyScores(compEntries)
    }

    // Reverse direction of the same ambiguity (questions -> answers): the composite ownership FK
    // gives the pair two relationships, so the embed needs the FK hint or qaData comes back null
    // and the whole Q&A review (rewrite coach, save-to-story-bank) silently never renders.
    const { data: qaData } = await supabase
      .from('questions')
      .select(
        'id, sequence_number, question_text, is_follow_up, competency, answers!answers_question_id_fkey(id, answer_text, word_count)'
      )
      .eq('session_id', sid)
      .order('sequence_number')

    if (qaData) {
      const rows = qaData as unknown as QAJoinRow[]
      const mappedItems = rows
        .filter((q) => q.answers.length > 0)
        .map((q) => {
          const answer = q.answers[0] as { id: string; answer_text: string; word_count: number }
          return {
            id: q.id,
            answerId: answer.id,
            sequenceNumber: q.sequence_number,
            questionText: q.question_text,
            answerText: answer.answer_text,
            wordCount: answer.word_count,
            isFollowUp: q.is_follow_up,
            competency: q.competency,
          }
        })
      setQaItems(mappedItems)

      // Pre-load any rewrites already generated for this session (cached in DB)
      const mainAnswerIds = mappedItems
        .filter((item) => !item.isFollowUp)
        .map((item) => item.answerId)
      if (mainAnswerIds.length > 0) {
        const { data: existingRewrites } = await supabase
          .from('answer_rewrites')
          .select('answer_id, example_answer, what_makes_it_strong')
          .in('answer_id', mainAnswerIds)
        if (existingRewrites && existingRewrites.length > 0) {
          setRewriteStates(
            new Map(
              existingRewrites.map((r) => [
                r.answer_id as string,
                {
                  status: 'done' as const,
                  data: {
                    example_answer: r.example_answer as string,
                    what_makes_it_strong: r.what_makes_it_strong as string,
                  },
                },
              ])
            )
          )
        }
      }
    }

    setLoading(false)
    void fetchReport(sid)
  }

  if (loading) {
    return (
      <main className="flex min-h-screen items-center justify-center p-8">
        <p className="text-sm text-muted-foreground">Loading your results…</p>
      </main>
    )
  }

  if (error || !session) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center p-8 text-center space-y-4">
        <p className="text-sm text-destructive">{error ?? 'Session not found.'}</p>
        <Link
          to="/dashboard"
          className="text-sm text-primary underline underline-offset-4 hover:opacity-80"
        >
          Back to dashboard
        </Link>
      </main>
    )
  }

  const competencyLabel = COMPETENCY_LABELS[session.competency] ?? session.competency
  const difficultyLabel = DIFFICULTY_LABELS[session.difficulty] ?? session.difficulty

  return (
    <main className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 border-b border-border bg-background/90 backdrop-blur-sm px-4 py-3 flex items-center justify-between">
        <Link
          to="/dashboard"
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          ← Dashboard
        </Link>
        <Link
          to="/interview/setup"
          className="rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
        >
          Practice again
        </Link>
      </header>

      <div className="max-w-2xl mx-auto px-4 py-8 space-y-8">
        {/* Session summary */}
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Session complete</h1>
          <p className="text-sm text-muted-foreground">
            {session.target_role} · {competencyLabel} · {difficultyLabel}
          </p>
        </div>

        {/* Overall score */}
        {scores ? (
          <div className="rounded-2xl border border-border bg-muted/40 p-6 flex items-center gap-6">
            <div className="text-center min-w-[72px]">
              <p className={cn('text-5xl font-bold tabular-nums', scoreColor(scores.overall))}>
                {scores.overall.toFixed(1)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">out of 5.0</p>
            </div>
            <div className="flex-1 space-y-1">
              <p className="text-sm font-semibold">Overall STAR score</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Weighted average across Situation, Task, Action, and Result components.
              </p>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-border bg-muted/40 p-6 text-center">
            <p className="text-sm text-muted-foreground">No scores found for this session.</p>
          </div>
        )}

        {/* STAR breakdown */}
        {scores && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">STAR breakdown</h2>
            <div className="rounded-xl border border-border bg-background divide-y divide-border overflow-hidden">
              {STAR_COMPONENTS.map(({ key, label, weight }) => (
                <ScoreRow key={key} label={label} weight={weight} score={scores[key] as number} />
              ))}
            </div>
          </section>
        )}

        {/* Competency breakdown */}
        {competencyScores.length > 0 && (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Competency breakdown</h2>
            <div className="rounded-xl border border-border bg-background divide-y divide-border overflow-hidden">
              {competencyScores.map((entry) => (
                <ScoreRow key={entry.competency} label={entry.label} score={entry.score} />
              ))}
            </div>
          </section>
        )}

        {/* Q&A review — Pro only */}
        {isPro ? (
          qaItems.length > 0 && (
            <QASection
              items={qaItems}
              rewriteStates={rewriteStates}
              onRequestRewrite={requestRewrite}
              saveStates={saveStates}
              onRequestSave={requestSave}
            />
          )
        ) : (
          <section className="space-y-3">
            <h2 className="text-sm font-semibold text-foreground">Your answers</h2>
            <div className="relative rounded-xl border border-border overflow-hidden">
              {/* Blurred preview rows */}
              <div className="divide-y divide-border opacity-25 select-none pointer-events-none">
                {[
                  'Tell me about a time you led a project under uncertainty.',
                  'Describe a situation where you had to influence without authority.',
                  'Give me an example of a time you had to adapt quickly.',
                ].map((q) => (
                  <div key={q} className="flex items-start gap-3 px-4 py-3">
                    <span className="w-6 text-xs font-mono text-muted-foreground mt-0.5">Q1</span>
                    <span className="flex-1 text-sm text-foreground">{q}</span>
                  </div>
                ))}
              </div>
              {/* Lock overlay */}
              <div className="absolute inset-0 bg-background/70 backdrop-blur-[2px] flex flex-col items-center justify-center gap-2 text-center px-6 py-8">
                <p className="text-sm font-semibold">
                  Answer review + example coaching — Pro feature
                </p>
                <p className="text-xs text-muted-foreground">
                  Review your exact answers, get a model example answer for every question, and save
                  your best answers straight to your Story Bank.
                </p>
                <Link
                  to="/settings"
                  className="mt-1 rounded-lg bg-primary px-4 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity"
                >
                  Upgrade to Pro
                </Link>
              </div>
            </div>
          </section>
        )}

        {/* AI report */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold text-foreground">Feedback report</h2>

          {reportLoading && !report && (
            <div className="rounded-xl border border-border bg-background px-4 py-6 text-center">
              <p className="text-sm text-muted-foreground">Generating your feedback…</p>
            </div>
          )}

          {!reportLoading && !report && reportError && (
            <div className="rounded-xl border border-border bg-background px-4 py-4 flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">{reportError}</p>
              {sessionId && (
                <button
                  type="button"
                  onClick={() => void fetchReport(sessionId)}
                  className="flex-shrink-0 rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
                >
                  Try again
                </button>
              )}
            </div>
          )}

          {!reportLoading && !report && !reportError && (
            <div className="rounded-xl border border-border bg-background px-4 py-4">
              <p className="text-sm text-muted-foreground">
                Detailed feedback is generated after your session is fully processed. Check back in
                a moment or start a new session.
              </p>
            </div>
          )}

          {report && (
            <div className="rounded-xl border border-border bg-background divide-y divide-border overflow-hidden">
              {/* Summary */}
              {report.summary_text && (
                <div className="px-4 py-4 space-y-1">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Summary
                  </p>
                  <p className="text-sm leading-relaxed">{report.summary_text}</p>
                </div>
              )}

              {/* Strengths */}
              {report.feedback_json?.strengths?.length > 0 && (
                <div className="px-4 py-4 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Strengths
                  </p>
                  <ul className="space-y-1.5">
                    {report.feedback_json.strengths.map((s) => (
                      <li key={s.slice(0, 40)} className="flex gap-2 text-sm">
                        <span className="text-green-500 mt-0.5 flex-shrink-0">✓</span>
                        <span className="leading-relaxed">{s}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Improvements */}
              {report.feedback_json?.improvements?.length > 0 && (
                <div className="px-4 py-4 space-y-2">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Areas to improve
                  </p>
                  <ul className="space-y-1.5">
                    {report.feedback_json.improvements.map((imp) => (
                      <li key={imp.slice(0, 40)} className="flex gap-2 text-sm">
                        <span className="text-amber-500 mt-0.5 flex-shrink-0">→</span>
                        <span className="leading-relaxed">{imp}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Rewrite tip */}
              {report.feedback_json?.rewrite_tip && (
                <div className="px-4 py-4 space-y-1.5">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Tip for next time
                  </p>
                  <p className="text-sm leading-relaxed text-muted-foreground">
                    {report.feedback_json.rewrite_tip}
                  </p>
                </div>
              )}

              {/* Example stronger result */}
              {report.feedback_json?.example_stronger_result && (
                <div className="px-4 py-4 space-y-1.5 bg-muted/30">
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Example stronger Result statement
                  </p>
                  <p className="text-sm leading-relaxed italic text-foreground">
                    "{report.feedback_json.example_stronger_result}"
                  </p>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Bottom CTA */}
        <div className="flex gap-3 pt-2">
          <Link
            to="/interview/setup"
            className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-primary-foreground text-center hover:opacity-90 transition-opacity"
          >
            Practice again
          </Link>
          <Link
            to="/dashboard"
            className="flex-1 rounded-lg border border-border py-2.5 text-sm font-medium text-center hover:bg-muted transition-colors"
          >
            Dashboard
          </Link>
        </div>
      </div>
    </main>
  )
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface QASectionProps {
  items: QAItem[]
  rewriteStates: Map<string, RewriteState>
  onRequestRewrite: (item: QAItem) => void
  saveStates: Map<string, 'idle' | 'saving' | 'saved' | 'error'>
  onRequestSave: (item: QAItem, title: string) => void
}

function QASection({
  items,
  rewriteStates,
  onRequestRewrite,
  saveStates,
  onRequestSave,
}: QASectionProps) {
  const [openIds, setOpenIds] = useState<Set<string>>(new Set())
  const allOpen = openIds.size === items.length

  function toggle(id: string) {
    setOpenIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  function toggleAll() {
    setOpenIds(allOpen ? new Set() : new Set(items.map((i) => i.id)))
  }

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-foreground">Your answers</h2>
        <button
          type="button"
          onClick={toggleAll}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <div className="rounded-xl border border-border bg-background divide-y divide-border overflow-hidden">
        {items.map((item) => {
          const isOpen = openIds.has(item.id)
          return (
            <div key={item.id}>
              <button
                type="button"
                onClick={() => toggle(item.id)}
                className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-muted/40 transition-colors"
              >
                <span className="flex-shrink-0 w-6 text-xs font-mono text-muted-foreground mt-0.5">
                  {item.isFollowUp ? '↳' : `Q${item.sequenceNumber}`}
                </span>
                <span className="flex-1 text-sm leading-relaxed text-foreground">
                  {item.questionText}
                </span>
                <ChevronIcon open={isOpen} />
              </button>
              {isOpen && (
                <div
                  className={cn(
                    'px-4 pb-4 space-y-2 bg-muted/20',
                    item.isFollowUp ? 'pl-14' : 'pl-13'
                  )}
                >
                  <p className="text-sm leading-relaxed text-foreground whitespace-pre-wrap">
                    {item.answerText}
                  </p>
                  <p className="text-xs text-muted-foreground">{item.wordCount} words</p>
                  {!item.isFollowUp && item.wordCount > 0 && (
                    <RewriteCoachSection
                      rewriteState={rewriteStates.get(item.answerId) ?? { status: 'idle' }}
                      onRequest={() => onRequestRewrite(item)}
                    />
                  )}
                  {!item.isFollowUp && (
                    <SaveToStoryBankSection
                      item={item}
                      saveState={saveStates.get(item.answerId) ?? 'idle'}
                      onSave={onRequestSave}
                    />
                  )}
                </div>
              )}
            </div>
          )
        })}
      </div>
    </section>
  )
}

interface RewriteCoachSectionProps {
  rewriteState: RewriteState
  onRequest: () => void
}

function RewriteCoachSection({ rewriteState, onRequest }: RewriteCoachSectionProps) {
  if (rewriteState.status === 'idle') {
    return (
      <div className="pt-1">
        <button
          type="button"
          onClick={onRequest}
          className="rounded-lg bg-lime px-3 py-1.5 text-xs font-semibold text-lime-foreground hover:opacity-90 transition-opacity"
        >
          See example answer
        </button>
      </div>
    )
  }

  if (rewriteState.status === 'loading') {
    return <p className="text-xs text-muted-foreground pt-1">Generating example…</p>
  }

  if (rewriteState.status === 'error') {
    return (
      <div className="pt-1">
        <button
          type="button"
          onClick={onRequest}
          className="rounded-lg border border-destructive/50 px-3 py-1.5 text-xs font-medium text-destructive hover:bg-destructive/10 transition-colors"
        >
          Try again
        </button>
      </div>
    )
  }

  if (!rewriteState.data) return null

  return (
    <div className="mt-2 rounded-xl border border-border bg-muted/30 divide-y divide-border overflow-hidden">
      <div className="px-4 py-3 space-y-1.5">
        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
          Example answer
        </p>
        <p className="text-sm leading-relaxed italic text-foreground">
          {rewriteState.data.example_answer}
        </p>
      </div>
      <div className="px-4 py-2">
        <p className="text-xs text-muted-foreground leading-relaxed">
          {rewriteState.data.what_makes_it_strong}
        </p>
      </div>
    </div>
  )
}

interface SaveToStoryBankSectionProps {
  item: QAItem
  saveState: 'idle' | 'saving' | 'saved' | 'error'
  onSave: (item: QAItem, title: string) => void
}

function SaveToStoryBankSection({ item, saveState, onSave }: SaveToStoryBankSectionProps) {
  const [open, setOpen] = useState(false)
  const [title, setTitle] = useState('')

  function handleOpen() {
    setTitle(item.questionText.slice(0, 60).replace(/[.?]$/, ''))
    setOpen(true)
  }

  function handleSave() {
    if (title.trim().length === 0) return
    onSave(item, title.trim())
    setOpen(false)
  }

  if (saveState === 'saved') {
    return (
      <p className="pt-1 text-xs text-emerald-600 dark:text-emerald-400 font-medium">
        ✓ Saved to Story Bank
      </p>
    )
  }

  if (!open) {
    return (
      <div className="pt-1">
        <button
          type="button"
          onClick={handleOpen}
          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
        >
          {saveState === 'error' ? 'Retry save' : 'Save to Story Bank'}
        </button>
      </div>
    )
  }

  return (
    <div className="pt-2 space-y-2">
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Story title…"
        maxLength={100}
        className="w-full rounded-lg border border-border bg-background px-3 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-ring"
        // biome-ignore lint/a11y/noAutofocus: inline form should focus on open for UX
        autoFocus
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave()
          if (e.key === 'Escape') setOpen(false)
        }}
      />
      <div className="flex gap-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saveState === 'saving' || title.trim().length === 0}
          className="rounded-lg bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saveState === 'saving' ? 'Saving…' : 'Save'}
        </button>
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="rounded-lg border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  )
}

function ChevronIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={cn(
        'flex-shrink-0 h-4 w-4 text-muted-foreground transition-transform mt-0.5',
        open && 'rotate-180'
      )}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  )
}

interface ScoreRowProps {
  label: string
  weight?: string
  score: number
}

function ScoreRow({ label, weight, score }: ScoreRowProps) {
  const pct = ((score - 1) / 4) * 100

  return (
    <div className="flex items-center gap-4 px-4 py-3">
      <div className="w-36 flex-shrink-0">
        <span className="text-sm font-medium">{label}</span>
        {weight && <span className="ml-1.5 text-xs text-muted-foreground">({weight})</span>}
      </div>
      <div
        className="flex-1 h-2 rounded-full bg-muted overflow-hidden"
        role="progressbar"
        tabIndex={-1}
        aria-valuenow={score}
        aria-valuemin={1}
        aria-valuemax={5}
        aria-label={`${label} score: ${score.toFixed(1)} out of 5`}
      >
        <div
          className={cn('h-full rounded-full transition-all', scoreBarColor(score))}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={cn('w-8 text-right text-sm font-semibold tabular-nums', scoreColor(score))}>
        {score.toFixed(1)}
      </span>
    </div>
  )
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 4.5) return 'text-green-500'
  if (score >= 3.5) return 'text-emerald-600 dark:text-emerald-400'
  if (score >= 2.5) return 'text-amber-600 dark:text-amber-400'
  return 'text-destructive'
}

function scoreBarColor(score: number): string {
  if (score >= 4.5) return 'bg-green-500'
  if (score >= 3.5) return 'bg-emerald-500'
  if (score >= 2.5) return 'bg-amber-500'
  return 'bg-destructive'
}
